import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField, useGallery } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';
import { GlowTrace } from '@vibesites/theme-preferred/components/GlowTrace';

// ============================================================================
// Photo selection — pick community/exterior/neighborhood images
// ============================================================================

const NEIGHBORHOOD_KEYWORDS = [
  'exterior', 'building', 'community', 'pool', 'courtyard', 'entrance',
  'lobby', 'lounge', 'outdoor', 'patio', 'rooftop', 'skyline', 'park',
  'neighborhood', 'street', 'aerial', 'facade', 'clubhouse', 'fitness',
  'gym', 'sunset', 'night', 'evening', 'view', 'balcony', 'terrace',
];

function scoreNeighborhood(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return NEIGHBORHOOD_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

/** Return gallery indices for the best neighborhood photos. */
function pickPhotoIndices(gallery: PhotoGallery[], count: number): number[] {
  if (!gallery.length) return [];

  const seen = new Set<string>();
  const unique: { index: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ index: i, score: scoreNeighborhood(photo) });
  }

  unique.sort((a, b) => b.score - a.score);
  const pool = unique.slice(0, Math.max(count * 2, 10));
  if (pool.length <= count) return pool.map((p) => p.index);

  const step = pool.length / count;
  const picked: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].index);
  }
  return picked;
}

function makeImageDef(label: string, index: number, defaultAlt: string): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `photo_gallery[${index}].image`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
        defaultValue: defaultAlt,
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const MOSAIC_IMAGE_SIZES =
  '(min-width: 1024px) 25vw, (min-width: 640px) 45vw, 90vw';

// ============================================================================
// Slot group definitions
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Neighborhood CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View the Neighborhood' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodCallout() {
  const ctaLink = useSlotGroup('neighborhoodcallout.cta_link', ctaLinkDef);

  const city = usePropertyField('property_address_city') as string | null;
  const state = usePropertyField('property_address_state') as string | null;
  const propertyName = usePropertyField('property_name') as string | null;

  const gallery = useGallery();
  const photoIndices = useMemo(() => pickPhotoIndices(gallery, 3), [gallery]);

  const imageADef = useMemo(
    () => makeImageDef('Mosaic Image A', photoIndices[0] ?? 0, 'Neighborhood'),
    [photoIndices],
  );
  const imageBDef = useMemo(
    () => makeImageDef('Mosaic Image B', photoIndices[1] ?? 1, 'Neighborhood'),
    [photoIndices],
  );
  const imageCDef = useMemo(
    () => makeImageDef('Mosaic Image C', photoIndices[2] ?? 2, 'Neighborhood'),
    [photoIndices],
  );

  const imageA = useSlotGroup('neighborhoodcallout.image_a', imageADef);
  const imageB = useSlotGroup('neighborhoodcallout.image_b', imageBDef);
  const imageC = useSlotGroup('neighborhoodcallout.image_c', imageCDef);

  useRevealStyles();
  const leftRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const rightRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  const cityName = city || 'the area';
  const stateName = state || '';
  const locationShort = stateName ? `${cityName}, ${stateName}` : cityName;

  const defaultTitle = `Explore ${cityName} step by step.`;
  const defaultDesc = `Ideally located in ${locationShort}, the neighborhood surrounding <strong>${propertyName || 'our community'}</strong> has so much to offer.\n\nEntertainment, restaurants, schools, & services combine to create a conveniently placed home. Check out the neighborhood!`;

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* Left: Photo mosaic grid */}
          <div ref={leftRef} className="reveal-fade-up">
            <div className="neighborhood-grid glow-trace-wrap relative rounded-sm overflow-hidden">
              <GlowTrace delay={0.5} />
              <div className="neighborhood-grid-a">
                <EditableSlotGroup groupPath="neighborhoodcallout.image_a" definition={imageADef}>
                  {imageA.image ? (
                    <VibesitesImage
                      url={imageA.image}
                      alt={imageA.alt || 'Neighborhood'}
                      sizes={MOSAIC_IMAGE_SIZES}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full min-h-[120px] bg-[var(--vibe-color-surface-muted,#f3f4f6)]" />
                  )}
                </EditableSlotGroup>
              </div>
              <div className="neighborhood-grid-b">
                <EditableSlotGroup groupPath="neighborhoodcallout.image_b" definition={imageBDef}>
                  {imageB.image ? (
                    <VibesitesImage
                      url={imageB.image}
                      alt={imageB.alt || 'Neighborhood'}
                      sizes={MOSAIC_IMAGE_SIZES}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full min-h-[120px] bg-[var(--vibe-color-surface-muted,#f3f4f6)]" />
                  )}
                </EditableSlotGroup>
              </div>
              <div className="neighborhood-grid-c">
                <EditableSlotGroup groupPath="neighborhoodcallout.image_c" definition={imageCDef}>
                  {imageC.image ? (
                    <VibesitesImage
                      url={imageC.image}
                      alt={imageC.alt || 'Neighborhood'}
                      sizes={MOSAIC_IMAGE_SIZES}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full min-h-[120px] bg-[var(--vibe-color-surface-muted,#f3f4f6)]" />
                  )}
                </EditableSlotGroup>
              </div>
            </div>
          </div>

          {/* Right: Copy + CTA */}
          <div ref={rightRef} className="reveal-fade-up">
            <RichTextContent
              path="neighborhoodcallout.eyebrow"
              label="Eyebrow"
              defaultValue="<p>Experiences Outside Your Doorstep</p>"
              as="div"
              className="font-body text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--vibe-color-primary)] mb-3 [&_p]:m-0 [&_h1]:m-0 [&_h2]:m-0"
            />

            <RichTextContent
              path="neighborhoodcallout.title"
              label="Title"
              defaultValue={`<p>${defaultTitle}</p>`}
              as="div"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[var(--vibe-color-text)] mb-6 tracking-tight leading-tight [&_p]:m-0 [&_h1]:m-0 [&_h2]:m-0"
            />

            <RichTextContent
              path="neighborhoodcallout.description"
              label="Description"
              defaultValue={`<p>${defaultDesc.replace(/\n\n/g, '</p><p>')}</p>`}
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed space-y-4 mb-8"
            />

            <EditableSlotGroup groupPath="neighborhoodcallout.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/neighborhood'}
                target={ctaLink._target || '_self'}
                className="neighborhood-cta-btn inline-flex items-center gap-2"
              >
                <span>{ctaLink.label || 'View the Neighborhood'}</span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Photo mosaic — 2×2 asymmetric grid */
        .neighborhood-grid {
          display: grid;
          grid-template-columns: 1fr 1.15fr;
          grid-template-rows: 1fr 1fr;
          gap: 4px;
          max-height: 380px;
        }
        .neighborhood-grid-a {
          grid-column: 1;
          grid-row: 1;
          overflow: hidden;
          min-height: 0;
        }
        .neighborhood-grid-b {
          grid-column: 2;
          grid-row: 1 / 3;
          overflow: hidden;
          min-height: 0;
        }
        .neighborhood-grid-c {
          grid-column: 1;
          grid-row: 2;
          overflow: hidden;
          min-height: 0;
        }
        .neighborhood-grid-a > *,
        .neighborhood-grid-b > *,
        .neighborhood-grid-c > * {
          display: block;
          height: 100%;
        }
        .neighborhood-grid-d {
          display: none;
        }

        @media (min-width: 640px) {
          .neighborhood-grid {
            gap: 5px;
            max-height: 420px;
          }
        }

        @media (min-width: 1024px) {
          .neighborhood-grid {
            max-height: 440px;
          }
        }

        .neighborhood-grid img {
          transition: transform 0.6s ease;
        }
        .neighborhood-grid img:hover {
          transform: scale(1.04);
        }

        /* CTA */
        .neighborhood-cta-btn {
          padding: 0.875rem 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary, white);
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        .neighborhood-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodCallout',
  displayName: 'Neighborhood Callout',
  tokens: {
    'neighborhoodcallout.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Experiences Outside Your Doorstep</p>',
    },
    'neighborhoodcallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Explore the neighborhood.</p>',
      suggestedFields: ['property_address_city'],
    },
    'neighborhoodcallout.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      suggestedFields: ['property_home_neighborhood_blurb', 'property_neighborhood_blurb'],
    },
  },
  slotGroups: {
    'neighborhoodcallout.image_a': {
      label: 'Mosaic Image A',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'Neighborhood' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'neighborhoodcallout.image_b': {
      label: 'Mosaic Image B',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: 'Neighborhood' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 1,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'neighborhoodcallout.image_c': {
      label: 'Mosaic Image C',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: 'Neighborhood' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 2,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'neighborhoodcallout.cta_link': {
      label: 'Neighborhood CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View the Neighborhood' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(NeighborhoodCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodCallout;
